import { InvestorData, InvestorProps, Opportunity, Role, SearchParams } from './propsTypes'

export type AccountStatus = 'ACTIVE' | 'INACTIVE' | 'DORMANT'

export type KYCStatus =
  | 'NOT_STARTED'
  | 'INITIATED'
  | 'PENDING'
  | 'REJECTED'
  | 'APPROVED'
  | 'RESUBMIT'

export const KycStatus: Record<KYCStatus, KYCStatus> = {
  NOT_STARTED: 'NOT_STARTED',
  INITIATED: 'INITIATED',
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  RESUBMIT: 'RESUBMIT',
}

export const WithdrawalRequestStatus = {
  INITIATED: 'INITIATED',
  ORDER_PLACED: 'ORDER_PLACED',
  REJECTED: 'REJECTED',
  CANCELLED: 'CANCELLED',
  TRADE_EXECUTED: 'TRADE_EXECUTED',
}

export const accountStatusMap: Record<AccountStatus, string> = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  DORMANT: 'DORMANT',
}

export interface WithdrawalSearchParamsV1 {
  investor_id?: number | string
  opportunity_id?: number | string
  order_id?: number | string
  search?: string
  sort?:
    | 'CLOSING_DATE_DESC' // default
    | 'CLOSING_DATE_ASC'
    // | 'TICKET_SIZE_DESC'
    // | 'TICKET_SIZE_ASC'
    | ''
  filter?: string
  offset: string
  limit: string
  status?: typeof WithdrawalRequestStatus[keyof typeof WithdrawalRequestStatus]
}

export interface KYCDetails {
  id: number
  created_at: string
  updated_at: string
  investor_id: number
  is_nda_accepted: boolean
  status: KYCStatus
  metadata: any
  investor_kyc_id: string
  investment_essentials_agreement: boolean
  accept_tob: boolean
  investor_portfolio_id: string
  transaction_checklist_agreement: boolean
  kyc_approval_date?: string
}

export interface KYCDetailsWithDocument extends KYCDetails {
  agreement_document_id: string | null
  self_declaration: boolean
}

export interface UserInvestmentDetails {
  canInvest: string
  investmentLimit: string
  kycStatus: string
  totalInvestment: string
}

// profile

/** Allow/deny for profile page buttons (from `actionGrants` / feature permissions). */
export interface ProfileActionAllow {
  updateInvestmentLimit: boolean
  resendVerificationLink: boolean
  blockInvestor: boolean
  canReadPortfolioStatements: boolean
  canWritePortfolioStatements: boolean
  addInvestorOpportunityTerms: boolean
  canReadInvestmentLimitRequests: boolean
}

export type ProfilePortfolioStatement = {
  id: string
  investorId: number
  originalFilename: string
  label: string | null
  uploadedBy: number
  createdAt: string
}

export interface ProfileDetailsPresenterProps {
  investorState: InvestorData
  investmentDetail: UserInvestmentDetails
  kycData: KYCDetailsWithDocument
  handleInvestmentLimit: (val: number) => void
  handleSendVerificationEmailLink: () => void
  handleBlockUnblock: (status: AccountStatus) => void
  actionAllow: ProfileActionAllow
  portfolioStatements: ProfilePortfolioStatement[]
  isLoadingPortfolioStatements: boolean
  isUploadingPortfolioStatement: boolean
  portfolioStatementsError: string
  onUploadPortfolioStatement: (file: File, label: string) => Promise<boolean>
  onDownloadPortfolioStatement: (statementId: string) => void
  onDeletePortfolioStatement: (statementId: string) => void
  investorBankDetails?: InvestorBankDetails
}

export interface InvestorKYCProps {
  kycData: KYCDetails
  investorState: InvestorData
  investmentDetail: UserInvestmentDetails
}

export interface InvestorBankDetails {
  accountNumber: string
  beneficiaryBank: string
  beneficiaryName: string
  swiftCode: string
  bankAddress: string
  cif: string
  sourceOfFunds: string[]
  sourceOfIncome: string[]
}

// orders

export interface Order {
  order_id: string
  opportunity_title: string
  order_creation_date: string | null
  investor_portfolio_id: string
  investor_name: string
  investor_email: string
  order_amount: number
  total_amount_to_be_transferred: number
  investment_completed: boolean
  opportunity_slug: string
  opportunity_id: number
  investor_id: number
  order_details_id: number
  completed_steps: string
  unit_allocation_date: string | null
  investor_account_status: AccountStatus
  welcome_letter_link: string
  agreement_document_id: string
  investor_kyc_id: string
  payment_copy: string
  transaction_reference_number: string
  order_status: string
  order_type: string
  withdrawal_completed?: boolean
}

export interface OrdersPresenterProps {
  pagination: {
    total: number
    limit: number
    count: number
    offset: number
  }
  handleCurrentPage: (currentPage: number) => void
  currentPage: number
  orders: Array<Order>
  resetSearchParams: () => void
  searchParams: {
    limit: string
    offset: string
    keyword: string
    filterBy: string
    orderStatus: string
    sortBy: string
  }
  handleSearchParams: ({ name, value }: { name: string; value: string }) => void
  handleOrderClick: (
    opportunitySlug: string,
    opportunityId: number | string,
    investorId: number | string,
    orderId: number | string,
    orderType: string,
  ) => void
}

export interface OrderCardProps {
  order_id: string
  opportunity_title: string
  order_creation_date: string | null
  investor_portfolio_id: string
  investor_name: string
  investor_email: string
  currency_code?: string
  order_amount: number
  total_amount_to_be_transferred: number
  investment_completed: boolean
  completed_steps: string
  unit_allocation_date: string | null
  investor_account_status: AccountStatus
  welcome_letter_link: string
  agreement_document_id: string
  investor_kyc_id: string
  payment_copy: string
  transaction_reference_number: string
  handleOrderClick: () => void
  order_status: string
  order_type: string
  withdrawal_completed?: boolean
  columnOrder?: string[]
}

// investors

export interface InvestorPropsV1 {
  account_status: AccountStatus
  created_at?: string
  email: string
  investment_limit: string
  investor_id: number
  investor_kyc_id?: string
  is_nda_accepted?: boolean
  is_verified?: boolean
  kycId?: number
  metadata?: any
  name: string
  role?: Role
  status?: KYCStatus
  total_investment?: string
  updated_at?: string
  investor_reference: string
}

export interface InvestorListPresenterProps {
  investors: InvestorPropsV1[]
  investorData: InvestorPropsV1
  pagination: {
    total: number
    limit: number
    count: number
    offset: number
  }
  currentPage: number
  searchParams: SearchParams
  handleCurrentPage: (currentPage: number) => void
  handleSearchParams: (params: { name: string; value: string }) => void
  resetSearchParams: () => void
  handleInvestorDataChange: (data: InvestorPropsV1) => void
  handleInvestorProfileClick: (email: string, investorId: number) => void
  handleInvestorInvestmentsClick: (email: string, investorId: number) => void
  handleInvestorWithdrawalClick: (email: string, investorId: number) => void
  handleUpdateInvestmentLimit: (investorId: number, investmentLimit: number) => void
}

// withdrawals

export interface Withdraw {
  id: number
  created_at: string
  sell_date: string
  quantity: number
  unit_sell_price: number
  opportunity_id: number
  opportunity_slug: string
  asset_class: string
  currency_code: string
  investor_id: number
  amount: string
  actual_amount_received?: number | null
  actual_amount_sent?: number | null
  actualAmountReceived?: number | null
  actualAmountSent?: number | null
  order_id: string
  metadata: {
    completedSteps?: number
    bankDetailsConfirmed?: boolean
    withdrawalInitiatedDate?: string
    withdrawalOrderPlacedDate?: string
    withdrawalApprovalDate?: string
    paymentReceivedDate?: string
    tradeExecutionDate?: string
    withdrawalCancelledDate?: string
    withdrawalRejectedDate?: string
    receivedAmount?: number
  }
  platform_fee: {
    amount: number
    currency_code: string
  }
  status: typeof WithdrawalRequestStatus[keyof typeof WithdrawalRequestStatus]
}

export interface AdminWithdrawalUpdatePayload {
  status: string
  actualAmountSent?: number
  quantity?: number
  metadata?: Record<string, unknown>
}

export interface WithdrawRequest extends Withdraw {
  opportunity_title: string
  investor_name: string
  investor_email: string
  investor_reference: string
}

export interface WithdrawalPresenterProps {
  opportunity: Opportunity
  withdrawals: WithdrawRequest[]
  currentPage: number
  pagination: {
    total: number
    limit: number
    count: number
    offset: number
  }
  totalWithdrawnAmount: number
  completedWithdrawalsCount: number
  searchParams: WithdrawalSearchParamsV1
  handleCurrentPage: (currentPage: number) => void
  handleSearchParams: ({ name, value }: { name: string; value: string }) => void
  handleInvestorClick: (investorId: number) => void
  resetSearchParams: () => void
}

export interface WithdrawalDetailPresenterProps {
  opportunity: Opportunity
  investorProfile: InvestorProps
  kycData: KYCDetailsWithDocument
  investorBankDetails: InvestorBankDetails
  withdrawalRequestDetail: WithdrawRequest
  /** When false, "Update Withdrawal Status" is disabled. */
  allowWithdrawalOrderStatusUpdates?: boolean
  handleGenerateKYCReportBtnClick: (kycId: string) => void
  handleStatus: ({
    value,
    field,
    description,
    units,
    actualAmountSent,
  }: {
    value: string | number
    field: string
    description: string
    units?: number
    actualAmountSent?: string
  }) => Promise<{ success: boolean; errorCode?: string; errorMessage?: string }>
}

export interface WithdrawalOpportunityListPresenterProps {
  investorProfile: InvestorProps
  searchParams: WithdrawalSearchParamsV1
  withdrawals: WithdrawRequest[]
  currentPage: number
  pagination: {
    total: number
    limit: number
    count: number
    offset: number
  }
  handleSearchParams: ({ name, value }: { name: string; value: string }) => void
  resetSearchParams: () => void
  handleCurrentPage: (currentPage: number) => void
}
